/**
 * WebSocketThinClientInterface
 * Implements ThinClientInterface using WebSocket communication with automatic
 * reconnection, heartbeat monitoring, and connection management.
 */

import { ErrorCodes } from '../utils/ErrorCodes';
import {
	CONNECTION_CHECK_INTERVAL_MS,
	CONNECTION_TIMEOUT_MS,
	ERROR_WEIGHT_VALUE,
	HEARTBEAT_INTERVAL_MS,
	RECONNECT_BASE_DELAY_MS,
	RECONNECT_MAX_DELAY_MS,
	REQUEST_ID_RANDOM_LENGTH,
	REQUEST_TIMEOUT_MS,
	WS_SUCCESS_ERROR_CODE,
	WS_URL,
} from './constants';
import ThinClientDetector from './ThinClientDetector';
import type {
	PrinterInfo,
	ThinClientInterface,
	WeightResult,
} from './ThinClientInterface';

type WebSocketRequest = {
	action: string;
	requestId: string;
	data?: Record<string, unknown>;
};

type WebSocketResponse = {
	action: string;
	requestId: string;
	data: unknown;
};

type PendingRequest = {
	resolve: (value: unknown) => void;
	reject: (reason: unknown) => void;
	timeout: ReturnType<typeof setTimeout>;
};

type ScaleUpdateCallback = (data: {
	weight: number;
	weightInOunces: number;
	weightInGrams: number;
	isStable: boolean;
	isConnected: boolean;
	hasError: boolean;
}) => void;

class WebSocketThinClientInterfaceClass implements ThinClientInterface {
	private ws: WebSocket | null = null;
	private pendingRequests: Map<string, PendingRequest> = new Map();
	private scaleUpdateCallback: ScaleUpdateCallback | null = null;
	private connectionPromise: Promise<WebSocket> | null = null;
	private handlerConfiguredFor: WebSocket | null = null;

	private heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;
	private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private connectionCheckIntervalId: ReturnType<typeof setInterval> | null =
		null;
	private reconnectAttempts = 0;
	private isManuallyDisconnected = false;

	/**
	 * Initialize the connection manager - start monitoring and auto-connect.
	 */
	init(): void {
		if (this.connectionCheckIntervalId) {
			return;
		}

		this.connectionCheckIntervalId = setInterval(() => {
			this.checkAndMaintainConnection();
		}, CONNECTION_CHECK_INTERVAL_MS);

		this.checkAndMaintainConnection();
	}

	/**
	 * Check connection health and reconnect if needed.
	 */
	private checkAndMaintainConnection(): void {
		if (this.isManuallyDisconnected) {
			return;
		}

		const existingWs = ThinClientDetector.getWebSocketConnection();
		if (existingWs && existingWs.readyState === WebSocket.OPEN) {
			if (this.ws !== existingWs) {
				this.ws = existingWs;
				this.setupMessageHandler(existingWs);
				this.handlerConfiguredFor = existingWs;
				this.startHeartbeat();
			}
			return;
		}

		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			return;
		}

		if (!this.connectionPromise) {
			this.reconnect();
		}
	}

	/**
	 * Start heartbeat to keep connection alive.
	 */
	private startHeartbeat(): void {
		this.stopHeartbeat();

		this.heartbeatIntervalId = setInterval(() => {
			if (this.ws && this.ws.readyState === WebSocket.OPEN) {
				try {
					this.ws.send(
						JSON.stringify({
							action: 'ping',
							requestId: `heartbeat-${Date.now()}`,
						})
					);
				} catch {
					this.handleDisconnect();
				}
			}
		}, HEARTBEAT_INTERVAL_MS);
	}

	/**
	 * Stop heartbeat.
	 */
	private stopHeartbeat(): void {
		if (this.heartbeatIntervalId) {
			clearInterval(this.heartbeatIntervalId);
			this.heartbeatIntervalId = null;
		}
	}

	/**
	 * Handle disconnect and schedule reconnect.
	 */
	private handleDisconnect(): void {
		this.stopHeartbeat();
		this.ws = null;
		this.handlerConfiguredFor = null;
		this.connectionPromise = null;

		this.pendingRequests.forEach(pending => {
			clearTimeout(pending.timeout);
			pending.reject(new Error('Connection lost'));
		});
		this.pendingRequests.clear();

		ThinClientDetector.setWebSocketConnection(null);

		if (!this.isManuallyDisconnected) {
			this.scheduleReconnect();
		}
	}

	/**
	 * Schedule reconnect with exponential backoff.
	 */
	private scheduleReconnect(): void {
		if (this.reconnectTimeoutId) {
			return;
		}

		const delay = Math.min(
			RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts),
			RECONNECT_MAX_DELAY_MS
		);

		this.reconnectTimeoutId = setTimeout(() => {
			this.reconnectTimeoutId = null;
			this.reconnect();
		}, delay);
	}

	/**
	 * Attempt to reconnect.
	 */
	private reconnect(): void {
		if (
			this.connectionPromise ||
			(this.ws && this.ws.readyState === WebSocket.OPEN)
		) {
			return;
		}

		this.reconnectAttempts++;

		this.connect()
			.then(() => {
				this.reconnectAttempts = 0;
			})
			.catch(() => {
				this.scheduleReconnect();
			});
	}

	/**
	 * Connect to WebSocket server.
	 */
	private connect(): Promise<WebSocket> {
		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			return Promise.resolve(this.ws);
		}

		if (this.connectionPromise) {
			return this.connectionPromise;
		}

		this.connectionPromise = new Promise<WebSocket>((resolve, reject) => {
			try {
				const ws = new WebSocket(WS_URL);

				const connectionTimeout = setTimeout(() => {
					this.connectionPromise = null;
					ws.close();
					reject(new Error('Connection timeout'));
				}, CONNECTION_TIMEOUT_MS);

				ws.onopen = () => {
					clearTimeout(connectionTimeout);

					this.ws = ws;
					this.connectionPromise = null;

					ThinClientDetector.setWebSocketConnection(ws);
					this.setupMessageHandler(ws);
					this.handlerConfiguredFor = ws;
					this.startHeartbeat();

					resolve(ws);
				};

				ws.onerror = error => {
					clearTimeout(connectionTimeout);
					console.error(
						'[WebSocketThinClientInterface] Connection error:',
						error
					);
					this.connectionPromise = null;
					reject(new Error('WebSocket connection failed'));
				};

				ws.onclose = () => {
					clearTimeout(connectionTimeout);

					if (this.ws === ws) {
						this.handleDisconnect();
					}
				};
			} catch (error) {
				this.connectionPromise = null;
				reject(error);
			}
		});

		return this.connectionPromise;
	}

	/**
	 * Ensure WebSocket connection is established.
	 */
	private async ensureConnection(): Promise<WebSocket> {
		const existingWs = ThinClientDetector.getWebSocketConnection();

		if (existingWs && existingWs.readyState === WebSocket.OPEN) {
			if (this.ws !== existingWs) {
				this.ws = existingWs;
			}
			if (this.handlerConfiguredFor !== existingWs) {
				this.setupMessageHandler(existingWs);
				this.handlerConfiguredFor = existingWs;
				this.startHeartbeat();
			}
			return existingWs;
		}

		if (this.ws && this.ws.readyState === WebSocket.OPEN) {
			return this.ws;
		}

		return this.connect();
	}

	/**
	 * Check if WebSocket is currently connected.
	 */
	isConnected(): boolean {
		return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
	}

	/**
	 * Setup message handler for WebSocket.
	 */
	private setupMessageHandler(ws: WebSocket): void {
		ws.onmessage = event => {
			try {
				const response: WebSocketResponse = JSON.parse(event.data);

				if (response.action === 'ping_response' || response.action === 'pong') {
					return;
				}

				if (response.action === 'scale_update' && this.scaleUpdateCallback) {
					this.scaleUpdateCallback(
						response.data as {
							weight: number;
							weightInOunces: number;
							weightInGrams: number;
							isStable: boolean;
							isConnected: boolean;
							hasError: boolean;
						}
					);
					return;
				}

				const pending = this.pendingRequests.get(response.requestId);

				if (pending) {
					clearTimeout(pending.timeout);
					this.pendingRequests.delete(response.requestId);
					pending.resolve(response.data);
				}
			} catch (error) {
				console.error(
					'[WebSocketThinClientInterface] Error parsing message:',
					error,
					'raw data:',
					event.data
				);
			}
		};
	}

	/**
	 * Send request and wait for response.
	 */
	private async sendRequest<T>(
		action: string,
		data?: Record<string, unknown>
	): Promise<T> {
		const ws = await this.ensureConnection();
		const requestId = this.generateRequestId();

		return new Promise((resolve, reject) => {
			const timeout = setTimeout(() => {
				this.pendingRequests.delete(requestId);
				reject(new Error(`Request timeout: ${action}`));
			}, REQUEST_TIMEOUT_MS);

			this.pendingRequests.set(requestId, {
				resolve: resolve as (value: unknown) => void,
				reject,
				timeout,
			});

			const request: WebSocketRequest = {
				action,
				requestId,
				data,
			};

			try {
				ws.send(JSON.stringify(request));
			} catch (error) {
				console.error('[WebSocketThinClientInterface] Send failed:', error);
				clearTimeout(timeout);
				this.pendingRequests.delete(requestId);
				this.handleDisconnect();
				reject(error instanceof Error ? error : new Error(String(error)));
			}
		});
	}

	/**
	 * Generate unique request ID.
	 */
	private generateRequestId(): string {
		const randomPart = Math.random()
			.toString(36)
			.substring(2, 2 + REQUEST_ID_RANDOM_LENGTH);
		return `${Date.now()}-${randomPart}`;
	}

	/**
	 * Get printer information.
	 */
	async getPrinterInfo(): Promise<PrinterInfo[]> {
		try {
			const response = await this.sendRequest<{ printer?: unknown[] }>(
				'get_printer_info'
			);
			const printers = response?.printer ?? [];
			return printers as PrinterInfo[];
		} catch (error) {
			console.error(
				'[WebSocketThinClientInterface] getPrinterInfo error:',
				error
			);
			return [];
		}
	}

	/**
	 * Get scale weight.
	 */
	async getWeight(): Promise<WeightResult> {
		try {
			const response = await this.sendRequest<{
				errorCode: number;
				weight: number;
				error?: string;
			}>('get_weight');

			return {
				errorCode:
					response.errorCode === WS_SUCCESS_ERROR_CODE
						? ErrorCodes.SUCCESS
						: ErrorCodes.plugin.SCALE_CALIBRATE_FAILED,
				weight: response.weight,
			};
		} catch (error) {
			console.error('[WebSocketThinClientInterface] getWeight error:', error);
			return {
				errorCode: ErrorCodes.plugin.SCALE_CALIBRATE_FAILED,
				weight: ERROR_WEIGHT_VALUE,
			};
		}
	}

	/**
	 * Reset (zero) the scale.
	 */
	async resetScale(): Promise<{ success: boolean }> {
		try {
			const response = await this.sendRequest<{ success: boolean }>(
				'zero_scale'
			);
			return response;
		} catch (error) {
			console.error('[WebSocketThinClientInterface] resetScale error:', error);
			return { success: false };
		}
	}

	/**
	 * Submit a print job.
	 */
	async print(
		printUrl: string,
		callback?: (result: string) => void
	): Promise<string> {
		try {
			const printCommandWithTabs = printUrl.replace(/\\t/g, '\t');
			const response = await this.sendRequest<{
				result?: string;
				error?: string;
			}>('submit_print_job', { printJob: printCommandWithTabs });

			const result = JSON.stringify(response);
			if (callback) callback(result);
			return result;
		} catch (error) {
			console.error('[WebSocketThinClientInterface] print error:', error);
			const errorResult = JSON.stringify({ error: String(error) });
			if (callback) callback(errorResult);
			return errorResult;
		}
	}

	/**
	 * Subscribe to scale updates.
	 */
	async subscribeToScale(callback: ScaleUpdateCallback): Promise<boolean> {
		try {
			this.scaleUpdateCallback = callback;
			await this.sendRequest('subscribe_scale');
			return true;
		} catch (error) {
			console.error(
				'[WebSocketThinClientInterface] subscribeToScale error:',
				error
			);
			return false;
		}
	}

	/**
	 * Unsubscribe from scale updates.
	 */
	async unsubscribeFromScale(): Promise<void> {
		this.scaleUpdateCallback = null;
		try {
			await this.sendRequest('unsubscribe_scale');
		} catch {
			/* no-op */
		}
	}

	/**
	 * Close WebSocket connection and stop all timers.
	 */
	close(): void {
		this.isManuallyDisconnected = true;
		this.stopHeartbeat();

		if (this.reconnectTimeoutId) {
			clearTimeout(this.reconnectTimeoutId);
			this.reconnectTimeoutId = null;
		}

		if (this.connectionCheckIntervalId) {
			clearInterval(this.connectionCheckIntervalId);
			this.connectionCheckIntervalId = null;
		}

		if (this.ws) {
			this.ws.close();
			this.ws = null;
			this.handlerConfiguredFor = null;
			ThinClientDetector.setWebSocketConnection(null);
		}

		this.pendingRequests.forEach(pending => {
			clearTimeout(pending.timeout);
			pending.reject(new Error('Connection closed'));
		});
		this.pendingRequests.clear();
		this.scaleUpdateCallback = null;
	}

	/**
	 * Reopen connection after manual close.
	 */
	reopen(): void {
		this.isManuallyDisconnected = false;
		this.reconnectAttempts = 0;
		this.init();
	}
}

const WebSocketThinClientInterface = new WebSocketThinClientInterfaceClass();

export { WebSocketThinClientInterface };
export default WebSocketThinClientInterface;
